/**
 * Holdfast survey knowledge (M8 plan MC-a10, D-360 RD7(d), `docs/story/factions.md` Holdfast): the Holdfast sell what
 * they know about the deep seams. This feature registers the Survey station tab, a `StationServicePanel` that is
 * available only at stations of a survey faction (`reach-explore.json survey.factions`, the Holdfast), so M2's station
 * screen shows it there through `bridge/m2/stationTabs.ts`. The tab lists the uncharted systems in range and sends the
 * exploration subsystem's `buySurvey` Reach command, which spends and charts (`exploration/commands.ts`).
 *
 * No Holdfast station sits in the home cluster (D-391), so the station-ui tab walk is unaffected. UI events only.
 */
import { z } from 'zod'
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import { loadExploreConfig, sellsSurvey, type ExploreConfig } from '../../exploration/config'
import { surveyTargets } from '../../exploration/commands'
import type { StationSite } from '../../galaxy/layout/types'
import type { PanelContext, StationServicePanel, StationServiceRegistry } from '../../reach/ports'
import type { ReachCore } from '../../reach/runtime/types'
import { formatCredits, groupDigits } from '../../reach/ui/services/format'
import { resultMessage, type InlineMessage, type PanelView, type ViewLine } from '../../reach/ui/services/model'
import { renderPanelView } from '../../reach/ui/services/panels'
import surveyJson from './survey.json'

const text = z.string().min(1)
export const surveyTextSchema = z.strictObject({
  comment: z.string().optional(),
  title: text,
  order: z.number(),
  intro: text,
  price: text,
  range: text,
  rangeValue: text,
  systems: text,
  heading: text,
  rumoured: text,
  unknown: text,
  buy: text,
  note: text,
  empty: text,
})
export type SurveyText = z.infer<typeof surveyTextSchema>

export function loadSurveyText(raw: unknown = surveyJson): SurveyText {
  return surveyTextSchema.parse(raw)
}

/** The Survey tab's view at `station`: the price, the range, the uncharted systems in range and the buy button. */
export function surveyView(core: ReachCore, station: StationSite, cfg: ExploreConfig, t: SurveyText): PanelView {
  const layout = core.session.layout
  const targets = layout === null ? [] : surveyTargets(core.state, core.galaxy, layout.systemId, cfg.survey.jumps)
  const price = layout === null ? 0 : cfg.survey.priceByRegion[layout.region]
  const lines: ViewLine[] = targets.map((id) => ({
    label: core.galaxy.systems.find((s) => s.id === id)?.name ?? id,
    value: core.state.knowledge[id] === 'rumoured' ? t.rumoured : t.unknown,
    tone: 'dim',
  }))
  return {
    summary: [
      { label: t.price, value: formatCredits(price) },
      { label: t.range, value: t.rangeValue.replace('{n}', groupDigits(cfg.survey.jumps)), tone: 'dim' },
      { label: t.systems, value: groupDigits(targets.length), tone: 'dim' },
    ],
    groups:
      targets.length === 0
        ? []
        : [
            { heading: null, lines: [{ label: t.intro, value: '' }], actions: [] },
            { heading: t.heading, lines, actions: [] },
            {
              heading: null,
              lines: [{ label: t.note, value: '', tone: 'dim' }],
              actions: [
                {
                  id: 'buy-survey',
                  label: `${t.buy} (${formatCredits(price)})`,
                  enabled: core.career.credits() >= price,
                  primary: true,
                  command: { kind: 'buySurvey', stationId: station.id },
                },
              ],
            },
          ],
    empty: targets.length === 0 ? t.empty : null,
  }
}

/** The Survey tab (available only at stations of a survey faction). */
export function createSurveyPanel(cfg: ExploreConfig = loadExploreConfig(), t: SurveyText = loadSurveyText()): StationServicePanel {
  return {
    id: 'survey',
    title: t.title,
    order: t.order,
    available: (station: StationSite): boolean => sellsSurvey(cfg, station.faction),
    mount(el: HTMLElement, ctx: PanelContext): () => void {
      let message: InlineMessage | null = null
      let mounted = true
      const render = (): void => renderPanelView(el, surveyView(ctx.core, ctx.station, cfg, t), message, (a) => {
        if (!mounted) return
        message = resultMessage(ctx.core.command(a.command))
        render()
        ctx.refresh()
      })
      render()
      return (): void => {
        mounted = false
        el.replaceChildren()
      }
    },
  }
}

/** Registries the tab already went into (the registry has no removal; a re-init must not register twice). */
const registered = new WeakSet<StationServiceRegistry>()

const feature: GameFeature = {
  name: 'holdfastSurvey',
  order: FEATURE_ORDER.career + 1,
  init(ctx: GameContext): void {
    const reach = ctx.reach
    if (reach === null || reach === undefined || registered.has(reach.services)) return
    registered.add(reach.services)
    reach.services.register(createSurveyPanel())
  },
}

export default feature
